import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  Shield,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { sanitizeHtml } from '../utils/sanitize';

/**
 * DocumentAcknowledgementModal
 * 
 * This component handles the document acknowledgement flow for all users.
 * It blocks the app if there are pending hard acknowledgements.
 * 
 * Props:
 * - onComplete: Called when all acknowledgements are completed
 */
const DocumentAcknowledgementModal = ({ onComplete, onDismiss }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [acknowledging, setAcknowledging] = useState(false);
  
  // Form state for acknowledgement
  const [typedName, setTypedName] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  
  // Error state
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchPendingAcknowledgements();
    }
  }, [user]);

  const fetchPendingAcknowledgements = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents/pending-acknowledgements');
      setPendingDocs(response.data.pending || []);
    } catch (error) {
      console.error('Failed to fetch pending acknowledgements:', error);
      // Don't block if we can't fetch - allow user to proceed
      setPendingDocs([]);
    } finally {
      setLoading(false);
    }
  };

  const currentDoc = pendingDocs[currentIndex];
  const isHard = currentDoc?.acknowledgement_type === 'hard';

  const validateForm = () => {
    setError('');
    
    if (!agreed) {
      setError('You must check the acknowledgement checkbox');
      return false;
    }
    
    if (!typedName.trim()) {
      setError('Please enter your full name');
      return false;
    }
    
    if (!typedEmail.trim()) {
      setError('Please enter your email address');
      return false;
    }
    
    // For hard acknowledgements, validate exact match
    if (isHard) {
      if (typedName.trim() !== user?.name?.trim()) {
        setError(`Your name must match exactly: "${user?.name}"`);
        return false;
      }
      if (typedEmail.trim().toLowerCase() !== user?.email?.trim().toLowerCase()) {
        setError(`Your email must match exactly: "${user?.email}"`);
        return false;
      }
    }
    
    return true;
  };

  const handleAcknowledge = async () => {
    if (!validateForm()) return;
    
    setAcknowledging(true);
    setError('');
    
    try {
      await api.post(`/documents/legal/${currentDoc.id}/acknowledge`, {
        typed_name: typedName.trim(),
        typed_email: typedEmail.trim()
      });
      
      toast.success(`Document "${currentDoc.title}" acknowledged`);
      
      // Move to next document or complete
      if (currentIndex < pendingDocs.length - 1) {
        setCurrentIndex(currentIndex + 1);
        // Reset form for next document
        setTypedName('');
        setTypedEmail('');
        setAgreed(false);
      } else {
        // All done
        onComplete?.();
        setPendingDocs([]);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to acknowledge document';
      setError(detail);
      toast.error(detail);
    } finally {
      setAcknowledging(false);
    }
  };

  // Don't render if loading or no pending docs
  if (loading) {
    return (
      <Dialog open={true}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
            <p className="text-slate-600">Checking for required documents...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (pendingDocs.length === 0) {
    return null;
  }

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open && onDismiss) onDismiss(); }}>
      <DialogContent 
        className="sm:max-w-2xl max-h-[90vh] flex flex-col"
        onPointerDownOutside={(e) => { if (!onDismiss) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!onDismiss) e.preventDefault(); }}
        data-testid="document-acknowledgement-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isHard ? 'bg-red-100' : 'bg-amber-100'}`}>
              {isHard ? (
                <Shield className="h-6 w-6 text-red-600" />
              ) : (
                <FileText className="h-6 w-6 text-amber-600" />
              )}
            </div>
            <div>
              <DialogTitle className="text-xl">
                Document Acknowledgement Required
              </DialogTitle>
              <DialogDescription className="mt-1">
                {pendingDocs.length > 1 ? (
                  <span>Document {currentIndex + 1} of {pendingDocs.length}</span>
                ) : (
                  <span>Please review and acknowledge this document to continue</span>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Document Info */}
          <div className="mb-4 p-4 bg-slate-50 rounded-lg border">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg text-slate-900">{currentDoc.title}</h3>
                {currentDoc.description && (
                  <p className="text-sm text-slate-600 mt-1">{currentDoc.description}</p>
                )}
              </div>
              <Badge className={isHard ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                {isHard ? 'Signature Required' : 'Acknowledgement Required'}
              </Badge>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              Version {currentDoc.version} • Updated {new Date(currentDoc.updated_at).toLocaleDateString()}
            </div>
          </div>

          {/* Document Content */}
          <div className="flex-1 min-h-0 border rounded-lg bg-white overflow-hidden">
            <div className="h-[280px] overflow-y-auto p-4">
              {currentDoc.document_type === 'pdf' && currentDoc.file_url ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">This document is a PDF file.</p>
                  <Button
                    variant="outline"
                    onClick={() => window.open(currentDoc.file_url, '_blank')}
                    data-testid="view-pdf-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open PDF in New Tab
                  </Button>
                </div>
              ) : (
                <div 
                  className="prose prose-sm max-w-none prose-headings:font-semibold prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentDoc.content || '<p>No content available</p>') }}
                />
              )}
            </div>
          </div>

          {/* Acknowledgement Form */}
          <div className="mt-4 space-y-4 border-t pt-4">
            {isHard && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium">Electronic Signature Required</p>
                    <p className="mt-1">
                      To acknowledge this document, you must type your <strong>exact name</strong> and <strong>email address</strong> as they appear in your profile.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="typed-name">
                  Full Name {isHard && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id="typed-name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder={isHard ? `Type exactly: ${user?.name}` : 'Enter your full name'}
                  className={isHard && typedName && typedName !== user?.name ? 'border-red-300' : ''}
                  data-testid="acknowledgement-name-input"
                />
                {isHard && (
                  <p className="text-xs text-slate-500 mt-1">
                    Must match: <strong>{user?.name}</strong>
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="typed-email">
                  Email Address {isHard && <span className="text-red-500">*</span>}
                </Label>
                <Input
                  id="typed-email"
                  type="email"
                  value={typedEmail}
                  onChange={(e) => setTypedEmail(e.target.value)}
                  placeholder={isHard ? `Type exactly: ${user?.email}` : 'Enter your email'}
                  className={isHard && typedEmail && typedEmail.toLowerCase() !== user?.email?.toLowerCase() ? 'border-red-300' : ''}
                  data-testid="acknowledgement-email-input"
                />
                {isHard && (
                  <p className="text-xs text-slate-500 mt-1">
                    Must match: <strong>{user?.email}</strong>
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
              <Checkbox
                id="agree-checkbox"
                checked={agreed}
                onCheckedChange={setAgreed}
                data-testid="acknowledgement-checkbox"
              />
              <label htmlFor="agree-checkbox" className="text-sm text-slate-700 cursor-pointer">
                {isHard ? (
                  <>
                    I, <strong>{user?.name}</strong>, have read and understand the contents of this document. 
                    By typing my name and email above, I am providing my electronic signature and agree to be bound by its terms.
                  </>
                ) : (
                  <>
                    I acknowledge that I have read and reviewed this document. 
                    I understand the contents and any updates that have been made.
                  </>
                )}
              </label>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              {onDismiss && (
                <Button
                  variant="ghost"
                  onClick={onDismiss}
                  className="text-slate-500 hover:text-slate-700"
                  data-testid="remind-later-btn"
                >
                  Remind Me Later
                </Button>
              )}
              <span className="text-sm text-slate-400 hidden sm:inline">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
            <Button
              onClick={handleAcknowledge}
              disabled={acknowledging || !agreed}
              className={isHard ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
              data-testid="submit-acknowledgement-btn"
            >
              {acknowledging ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {isHard ? 'Sign & Acknowledge' : 'I Acknowledge'}
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentAcknowledgementModal;
